import { Button, useTranslation } from '@hbs-us/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { RoleFilterPills } from './components/RoleFilterPills.js';
import { UsersEmptyState } from './components/UsersEmptyState.js';
import { UsersTable } from './components/UsersTable.js';
import type { UserListItemDto, UsersResponse } from './contractsMirror.js';
import type { UsersSearch } from './searchParams.js';
import { useUserPatchMutation } from './useUsersQuery.js';

/**
 * The redesigned People & Access directory (UI-SPEC archetype A — list + detail) on the
 * locked Velocity brand. Page wrapper + header (title, subtitle, the "+ Add User" invite
 * CTA + a "Permissions" link to the matrix + a "My Account" link) + a labelled search
 * input + the role-filter pill row (server counts) + the semantic users table — or the
 * empty state.
 *
 * The coarse endpoint (22-06) owns the role/active counts; this view renders the returned
 * payload and applies the free-text `?q` + `?role` filters IN-MEMORY over the
 * already-loaded directory (small dataset, no extra round-trip). The deactivate/restore
 * action routes through the focus-trapped `ConfirmDialog` (Shared 7 — never a native
 * confirm). The privilege-grant matrix lives on its own page (PermissionsMatrixView).
 */

export interface UsersViewProps {
  data: UsersResponse;
  search: UsersSearch;
}

/** Case-insensitive match of the search text against name / email. */
function matchesQuery(user: UserListItemDto, q: string): boolean {
  const needle = q.toLowerCase();
  return user.name.toLowerCase().includes(needle) || user.email.toLowerCase().includes(needle);
}

export function UsersView({ data, search }: UsersViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const users = data.users ?? [];
  const roleCounts = data.roleCounts ?? {};
  const totalUsers = data.totalUsers ?? users.length;

  // Controlled search input; the URL `?q` is the source of truth (deep-linkable).
  const [queryText, setQueryText] = useState(search.q ?? '');

  const filtered = useMemo(() => {
    const q = queryText.trim();
    return users.filter((user) => {
      if (search.role && user.role !== search.role) return false;
      if (q && !matchesQuery(user, q)) return false;
      return true;
    });
  }, [users, queryText, search.role]);

  // The deactivate/restore confirm target (Shared 7 ConfirmDialog).
  const [confirmTarget, setConfirmTarget] = useState<UserListItemDto | null>(null);
  const patchMutation = useUserPatchMutation();

  // A guard rejection (self-deactivation 400, last-active-Admin 409) surfaces inline.
  const guardError =
    patchMutation.error instanceof ApiError
      ? t(`users:deactivateDialog.errors.${patchMutation.error.message}`, {
          defaultValue: t('users:deactivateDialog.errors.generic'),
        })
      : null;

  const hasData = filtered.length > 0;
  const searching = queryText.trim().length > 0 || Boolean(search.role);

  function goToEdit(user: UserListItemDto) {
    void navigate({ to: '/users/$id', params: { id: String(user.id) } });
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('users:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">{t('users:subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="secondary">
            <Link to="/account">{t('users:actions.myAccount')}</Link>
          </Button>
          <Button asChild variant="secondary">
            <Link to="/users/permissions">{t('users:actions.permissions')}</Link>
          </Button>
          <Button asChild>
            <Link to="/users/new">{t('users:actions.addUser')}</Link>
          </Button>
        </div>
      </header>

      <div className="mb-4">
        <label htmlFor="user-search" className="sr-only">
          {t('users:search.label')}
        </label>
        <div className="relative max-w-md">
          <Search
            className="pointer-events-none absolute top-3.5 left-3 size-4 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            id="user-search"
            type="search"
            value={queryText}
            onChange={(event) => setQueryText(event.target.value)}
            placeholder={t('users:search.placeholder')}
            className="h-11 w-full rounded-md border border-border bg-card pr-3 pl-9 text-foreground text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>
      </div>

      <RoleFilterPills search={search} roleCounts={roleCounts} totalUsers={totalUsers} />

      {hasData ? (
        <UsersTable users={filtered} onEdit={goToEdit} onDeactivate={setConfirmTarget} />
      ) : (
        <UsersEmptyState searching={searching} />
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        heading={
          confirmTarget?.active === 0
            ? t('users:deactivateDialog.restoreHeading')
            : t('users:deactivateDialog.heading')
        }
        body={
          confirmTarget?.active === 0
            ? t('users:deactivateDialog.restoreBody')
            : t('users:deactivateDialog.body', { name: confirmTarget?.name ?? '' })
        }
        confirmLabel={
          confirmTarget?.active === 0
            ? t('users:deactivateDialog.restoreConfirm')
            : t('users:deactivateDialog.confirm')
        }
        cancelLabel={t('users:deactivateDialog.cancel')}
        confirmPending={patchMutation.isPending}
        errorMessage={guardError}
        onCancel={() => {
          patchMutation.reset();
          setConfirmTarget(null);
        }}
        onConfirm={() => {
          // Toggle the user's active flag via the api-host write (Plan 22-24). A guard
          // rejection (self-deactivation 400 / last-active-Admin 409) surfaces inline; the
          // dialog stays open so the operator sees why. On success the directory re-syncs.
          const target = confirmTarget;
          if (!target) return;
          patchMutation.mutate(
            { userId: target.id, active: target.active === 0 },
            { onSuccess: () => setConfirmTarget(null) },
          );
        }}
      />
    </div>
  );
}
